import emit from '../../common/events';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { FreeTrialPurchaseConverter } from './utils/free-trial-conversion.util';
import { Modal } from '../modal/modal';
import { Product } from '../cart/shop-cart/interfaces/shop-cart.interface';
import { StoreState } from '../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement, property } from 'lit/decorators.js';
import { getAllFreeTrialProducts } from './utils/free-trial.util';
import { getAnalyticsSafeData, selectUser } from '../user-profile/user-profile.selectors';
import { getAppFlow } from '../user-profile/utils/user-profile.util';
import { getProductsWithAdditionalAnalytics } from '../../common/utils/analytics-util';
import { html, TemplateResult } from 'lit';

/**
 * Removes (reverts) a free trial for a product in the cart.
 */
@customElement('mhecomm-free-trial-remove-from-cart')
export class FreeTrialRemoveFromCart extends connect(store)(BaseComponent) {
  /**
   * The isbn of the cart item line, used as unique identifier.
   */
  @property()
  isbn?: string;

  /**
   * The instance for the Bootstrap modal.
   */
  private readonly modal: Modal;

  /**
   * The converter utility to convert free trial items to prchase items.
   */
  private freeTrialPurchaseConverter: FreeTrialPurchaseConverter;

  constructor() {
    super();
    this.freeTrialPurchaseConverter = new FreeTrialPurchaseConverter();
    this.modal = new Modal({
      modalContext: {
        component: this,
        componentRef: 'remove-free-trial',
        componentRefData: this.getAnalyticDetails(
          'close-remove-free-trial-from-cart-modal',
          'Close Remove Free Trial From Cart Modal'
        ),
      },
      header: 'Remove Free Trial',
      body: { template: this.getModalContent() },
      primaryButton: { label: 'Confirm' },
    });
  }

  renderComp() {
    return html`
      <span class="alert-success fs-small">
        Promo codes cannot be combined with free trials. If you would like to use a promo code instead, please click
        <button
          type="button"
          id="btn-remove-free-trial-${this.isbn}"
          data-automation-id="btn-remove-free-trial-${this.isbn}"
          class="btn btn-link border-0 p-0 fs-small align-baseline"
          aria-label="Remove free trial products from cart, opens in a modal"
          @click=${this.openModal}
        >
          here</button
        >.
      </span>
    `;
  }

  /**
   * Opens the modal.
   */
  private openModal(): void {
    this.emitFreeTrialRemoveEvent('open-remove-free-trial-from-cart-modal', 'Open Remove Free Trial From Cart Modal');
    this.modal.open();
    this.addTrackedEventListener(this.modal, 'mhecomm-modal-confirmed', this.removeFreeTrial.bind(this));
  }

  /**
   * Returns the modal content.
   */
  private getModalContent(): TemplateResult {
    return html`
      Clicking confirm will remove any free trials currently in your cart and apply the full price to your selected
      products. You will be able to apply applicable promo codes in the cart after logging in. Would you like to
      proceed?
    `;
  }

  /**
   * Removes the free trial for all free trial products in the cart.
   */
  private async removeFreeTrial(event: Event): Promise<void> {
    event.preventDefault();
    const products = getAllFreeTrialProducts();
    const convertedProducts = this.freeTrialPurchaseConverter.convertProducts(products, true);

    this.modal!.close();
    this.emitFreeTrialRemoveEvent(
      'confirm-remove-free-trial-from-cart',
      'Confirm Remove Free Trial From Cart',
      products
    );

    if (convertedProducts.length > 0) {
      this.dispatchEvent(
        new CustomEvent('reload-cart-list', { bubbles: true, composed: true, detail: { timeout: 3000 } })
      );
      await this.freeTrialPurchaseConverter.purchaseProducts(convertedProducts);
    }
  }

  /**
   * Emits the remove free trial events (open modal/confirm removal).
   *
   * @param identifier
   * @param label
   * @param products
   *
   * @private
   */
  private emitFreeTrialRemoveEvent(identifier: string, label: string, products?: Product[]): void {
    emit({
      name: 'click',
      element: this,
      detail: this.getAnalyticDetails(identifier, label, products),
    });
  }

  /**
   * Returns analytic details for the different remove free trial events.
   *
   * @param identifier
   * @param label
   * @param products
   * @private
   */
  private getAnalyticDetails(identifier: string, label: string, products?: Product[]) {
    return {
      eventName: identifier,
      comp: 'mhecomm-free-trial-remove-from-cart',
      label: label,
      appFlow: getAppFlow(),
      user: getAnalyticsSafeData(selectUser(store.getState() as StoreState)),
      url: window.location.href,
      ...(products && { products: getProductsWithAdditionalAnalytics({ products }) }),
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-free-trial-remove-from-cart': FreeTrialRemoveFromCart;
  }
}
